import { computed, DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { injectMutation, injectQuery, injectQueryClient } from '@tanstack/angular-query-experimental';
import { lastValueFrom } from 'rxjs';

import { NotificationsApiService, NotificationsResponse } from './notifications-api.service';
import { SseService } from './sse.service';
import { Auth } from 'src/providers/auth/auth';

export const notificationKeys = {
  all: ['notifications'] as const,
  list: (page: number, perPage: number) => [...notificationKeys.all, 'list', page, perPage] as const,
  badge: () => [...notificationKeys.all, 'badge'] as const,
};

@Injectable({ providedIn: 'root' })
export class NotificationsQueriesService {
  private readonly api = inject(NotificationsApiService);
  private readonly auth = inject(Auth);
  private readonly sseService = inject(SseService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly queryClient = injectQueryClient();

  readonly isSseConnected = computed(() => this.sseService.connectionState() === 'connected');

  readonly badgeQuery = injectQuery(() => ({
    queryKey: notificationKeys.badge(),
    queryFn: () => this.fetchBadgeNotifications(),
    refetchInterval: this.isSseConnected() ? false : 30_000,
    enabled: this.auth.isAuthenticated(),
  }));

  constructor() {
    this.sseService.on('notification.received')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.invalidateNotifications();
      });
  }

  readonly notificationCount = computed(() => {
    const data = this.badgeQuery.data();
    if (!data) {return 0;}
    return data.body.filter(n => !n.is_read).length;
  });

  readonly isLoadingBadge = computed(() => this.badgeQuery.isPending());

  readonly markAsReadMutation = injectMutation(() => ({
    mutationFn: (notificationId: number) => lastValueFrom(this.api.markAsRead(notificationId)),
    onSuccess: () => {
      this.queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  }));

  readonly markAllAsReadMutation = injectMutation(() => ({
    mutationFn: () => lastValueFrom(this.api.markAllAsRead()),
    onSuccess: () => {
      this.queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  }));

  private async fetchBadgeNotifications(): Promise<NotificationsResponse> {
    return lastValueFrom(this.api.getNotifications(1, 11));
  }

  async fetchNotifications(page: number, perPage: number): Promise<NotificationsResponse> {
    return lastValueFrom(this.api.getNotifications(page, perPage));
  }

  invalidateNotifications(): void {
    this.queryClient.invalidateQueries({ queryKey: notificationKeys.all });
  }

  refetchBadge(): void {
    this.badgeQuery.refetch();
  }

  decrementCount(amount = 1): void {
    const currentData = this.queryClient.getQueryData<NotificationsResponse>(notificationKeys.badge());
    if (currentData) {
      const updatedBody = currentData.body.map((n, index) => {
        if (index < amount && !n.is_read) {
          return { ...n, is_read: true };
        }
        return n;
      });
      this.queryClient.setQueryData(notificationKeys.badge(), {
        ...currentData,
        body: updatedBody,
      });
    }
  }
}
